var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from "react";
import { FloatingFocusManager, FloatingPortal, useDismiss, useFloating, useInteractions, useRole, useTransitionStyles, } from "@floating-ui/react";
import { QuiButton, QuiForm, QuiIcon } from "../../index.js";
import classNames from "classnames";
/** A sticky header displayed below the modal title bar and above the content. */
export function QuiModalHeader(_a) {
    var { children, className, padding } = _a, otherProps = __rest(_a, ["children", "className", "padding"]);
    return (React.createElement("div", Object.assign({ className: classNames(className, "qui-modal-header", getModalSectionPadding(padding)) }, otherProps), children));
}
/** Modal content container. It is scrollable for content that exceeds height. */
export function QuiModalContent(_a) {
    var { children, padding, className } = _a, otherProps = __rest(_a, ["children", "padding", "className"]);
    const contentClassName = classNames(className, "qui-modal-content", getModalSectionPadding(padding));
    return (React.createElement("div", Object.assign({ className: contentClassName }, otherProps), children));
}
/** A sticky footer displayed at the bottom of the modal. */
export function QuiModalFooter(_a) {
    var { className, padding, children } = _a, otherProps = __rest(_a, ["className", "padding", "children"]);
    return (React.createElement("div", Object.assign({ className: classNames(className, "qui-modal-footer", getModalSectionPadding(padding)) }, otherProps), children));
}
/** A form that can be used as a parent of other QuiModal components.
 * <QuiModalDrawer>
 *  <QuiModalForm>
 *      <QuiModalContent>
 *          <QuiTextField .../>
 *      </QuiModalContent>
 *      <QuiModalFooter>
 *          <QuiSubmitButton .../>
 *       </QuiModalFooter>
 *  </QuiModalForm>
 * </QuiModalDrawer>
 */
export function QuiModalForm(_a) {
    var { children } = _a, otherProps = __rest(_a, ["children"]);
    return (React.createElement(QuiForm, Object.assign({}, otherProps, { className: "qui-modal-form" }), children));
}
const ModalAnimationDurations = {
    drawer: 300,
    dialog: 120,
};
const ModalDrawerPlacementTransform = {
    top: ["translateY(-100%)", "translateY(0)"],
    bottom: ["translateY(100%)", "translateY(0)"],
    left: ["translateX(-100%)", "translateX(0)"],
    right: ["translateX(100%)", "translateX(0)"],
};
// eslint-disable-next-line sonarjs/cognitive-complexity
function QuiModal(_a) {
    var { children, className, disableDismissOnOutsideClick, disableDismissOnEscapeKeyDown, transitionDuration, icon, isOpen, onClose, title, placement, closeButtonPosition, kind, initialFocus } = _a, otherProps = __rest(_a, ["children", "className", "disableDismissOnOutsideClick", "disableDismissOnEscapeKeyDown", "transitionDuration", "icon", "isOpen", "onClose", "title", "placement", "closeButtonPosition", "kind", "initialFocus"]);
    const { refs, context } = useFloating({
        open: isOpen,
        onOpenChange: onClose,
    });
    const { isMounted, styles: backdropStyles } = useTransitionStyles(context, {
        duration: typeof transitionDuration === "number" ? transitionDuration : ModalAnimationDurations[kind],
    });
    const drawerAnimation = useTransitionStyles(context, {
        duration: typeof transitionDuration === "number" ? transitionDuration : ModalAnimationDurations[kind],
        initial: { transform: ModalDrawerPlacementTransform[placement !== null && placement !== void 0 ? placement : "right"][0] },
        common: { transform: ModalDrawerPlacementTransform[placement !== null && placement !== void 0 ? placement : "right"][1] },
    }).styles;
    const modalAnimationStyles = kind === "drawer" ? drawerAnimation : {};
    const role = useRole(context, {
        role: "dialog",
    });
    const dismiss = useDismiss(context, {
        escapeKey: disableDismissOnEscapeKeyDown !== true,
        outsidePress: disableDismissOnOutsideClick !== true,
    });
    const { getFloatingProps } = useInteractions([role, dismiss]);
    return (React.createElement(FloatingPortal, null, isMounted && (React.createElement(FloatingFocusManager, { context: context, initialFocus: initialFocus === undefined ? refs.floating : initialFocus },
        React.createElement("div", { style: backdropStyles, className: classNames(`qui-modal-backdrop qui-modal-backdrop--${kind}`, {
                [`qui-modal-backdrop--${placement !== null && placement !== void 0 ? placement : "right"}`]: kind === "drawer",
            }) },
            React.createElement("div", Object.assign({ ref: refs.setFloating, className: classNames(`qui-modal-wrapper qui-modal-wrapper--${kind}`, className, {
                    [`qui-modal-wrapper--${placement !== null && placement !== void 0 ? placement : "right"}`]: kind === "drawer",
                }) }, getFloatingProps(), otherProps, { style: Object.assign(Object.assign({}, otherProps.style), modalAnimationStyles) }),
                kind === "dialog" && closeButtonPosition === "outside" && (React.createElement("div", { className: "qui-modal-titlebar--floating" },
                    React.createElement(QuiButton, { title: "Close", className: "qui-modal-close-button qui-modal-close-button--floating", size: "sm", iconLeft: "x", onClick: onClose, tabIndex: disableDismissOnEscapeKeyDown ? 0 : -1 }))),
                React.createElement("div", { className: `qui-modal-inner qui-modal-inner--${kind}` },
                    ((kind === "dialog" && (title || icon)) || kind === "drawer") && (React.createElement("div", { className: "qui-modal-titlebar" },
                        React.createElement("div", { className: "qui-modal-titlebar-title" },
                            icon ? React.createElement(QuiIcon, { icon: icon }) : null,
                            title ? React.createElement(React.Fragment, null, title) : null),
                        ((kind === "dialog" && closeButtonPosition !== "outside") || kind === "drawer") && (React.createElement("div", null,
                            React.createElement(QuiButton, { variant: "minimal", className: "qui-modal-close-button qui-modal-close-button--titlebar", size: "sm", iconLeft: "x", onClick: onClose, title: "Close", tabIndex: disableDismissOnEscapeKeyDown ? 0 : -1 }))))),
                    kind === "dialog" && !icon && !title && closeButtonPosition !== "outside" && (React.createElement(QuiButton, { className: "qui-modal-close-button qui-modal-close-button--absolute", variant: "minimal", size: "sm", iconLeft: "x", onClick: onClose, title: "Close" })),
                    children)))))));
}
export function QuiModalDialog(props) {
    return React.createElement(QuiModal, Object.assign({}, props, { kind: "dialog" }));
}
export function QuiModalDrawer(props) {
    return React.createElement(QuiModal, Object.assign({}, props, { kind: "drawer" }));
}
function getModalSectionPadding(padding) {
    return padding ? `qui-padding-${padding.replace(" ", "-")}` : "qui-padding-sm-lg";
}
